import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import MiniTiptap from './MiniTiptap';

import type { HolisticRubricContent, RubricPerformanceLevel } from '@/shared/types/rubric';
import { useSortableReorderSensors } from '@/shared/hooks/useSortableReorderSensors';
import { reorderModifiers } from '@/shared/utils/dndModifiers';

const DEFAULT_LEVEL_LABELS = ['Beginning', 'Developing', 'Proficient', 'Advanced', 'Distinguished'];

interface Props {
  content: HolisticRubricContent;
  canEdit: boolean;
  onChange: (content: HolisticRubricContent) => void;
}

function sortedByPosition<T extends { position: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.position - b.position);
}

function nextPosition(items: { position: number }[]): number {
  return items.reduce((m, i) => Math.max(m, i.position), 0) + 1000;
}

// ── Sortable level card ───────────────────────────────────────────────────

interface SortableLevelCardProps {
  level: RubricPerformanceLevel;
  canEdit: boolean;
  levelsCount: number;
  onUpdateLabel: (id: string, label: string) => void;
  onUpdateDescription: (id: string, desc: string) => void;
  onRemove: (id: string) => void;
}

const SortableLevelCard: React.FC<SortableLevelCardProps> = ({
  level,
  canEdit,
  levelsCount,
  onUpdateLabel,
  onUpdateDescription,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: level.id,
  });

  const style: React.CSSProperties = {
    // Translate, not Transform: dnd-kit's post-drop FLIP transform carries scaleX/scaleY
    // that visibly squash rows whose measured box changed during the drag.
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          {canEdit && (
            <Box
              {...attributes}
              {...listeners}
              sx={{
                cursor: 'grab',
                color: 'text.disabled',
                flexShrink: 0,
                '&:hover': { color: 'text.secondary' },
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </Box>
          )}

          {canEdit ? (
            <TextField
              value={level.label}
              onChange={(e) => onUpdateLabel(level.id, e.target.value)}
              variant="standard"
              size="small"
              inputProps={{ style: { fontWeight: 600, fontSize: '1rem' } }}
              sx={{ flex: 1 }}
            />
          ) : (
            <Typography variant="body1" fontWeight={600} sx={{ flex: 1 }}>
              {level.label}
            </Typography>
          )}

          {canEdit && levelsCount > 1 && (
            <Tooltip title="Remove level">
              <IconButton size="small" color="error" onClick={() => onRemove(level.id)}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <MiniTiptap
          content={level.description ?? ''}
          editable={canEdit}
          onUpdate={(html) => onUpdateDescription(level.id, html)}
          placeholder="Describe what performance looks like at this level..."
          minHeight={80}
        />
      </CardContent>
    </Card>
  );
};

// ── Main component ────────────────────────────────────────────────────────

const HolisticEditor: React.FC<Props> = ({ content, canEdit, onChange }) => {
  const levels = useMemo(
    () => sortedByPosition(content.performanceLevels),
    [content.performanceLevels]
  );
  const sensors = useSortableReorderSensors();

  const addLevel = useCallback(() => {
    const idx = levels.length;
    const newLevel: RubricPerformanceLevel = {
      id: crypto.randomUUID(),
      label: DEFAULT_LEVEL_LABELS[idx] ?? `Level ${idx + 1}`,
      position: nextPosition(content.performanceLevels),
      description: '',
    };
    onChange({ ...content, performanceLevels: [...content.performanceLevels, newLevel] });
  }, [content, levels.length, onChange]);

  const removeLevel = useCallback(
    (levelId: string) => {
      onChange({
        ...content,
        performanceLevels: content.performanceLevels.filter((l) => l.id !== levelId),
      });
    },
    [content, onChange]
  );

  const updateLabel = useCallback(
    (levelId: string, label: string) => {
      onChange({
        ...content,
        performanceLevels: content.performanceLevels.map((l) =>
          l.id === levelId ? { ...l, label } : l
        ),
      });
    },
    [content, onChange]
  );

  const updateDescription = useCallback(
    (levelId: string, description: string) => {
      onChange({
        ...content,
        performanceLevels: content.performanceLevels.map((l) =>
          l.id === levelId ? { ...l, description } : l
        ),
      });
    },
    [content, onChange]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const sorted = sortedByPosition(content.performanceLevels);
      const fromIdx = sorted.findIndex((l) => l.id === active.id);
      const toIdx = sorted.findIndex((l) => l.id === over.id);
      if (fromIdx < 0 || toIdx < 0) return;
      const reordered = arrayMove(sorted, fromIdx, toIdx);
      const newLevels = reordered.map((l, i) => ({ ...l, position: (i + 1) * 1000 }));
      onChange({ ...content, performanceLevels: newLevels });
    },
    [content, onChange]
  );

  return (
    <Box>
      <DndContext
        sensors={canEdit ? sensors : undefined}
        modifiers={reorderModifiers}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={levels.map((l) => l.id)} strategy={verticalListSortingStrategy}>
          {levels.map((level) => (
            <SortableLevelCard
              key={level.id}
              level={level}
              canEdit={canEdit}
              levelsCount={levels.length}
              onUpdateLabel={updateLabel}
              onUpdateDescription={updateDescription}
              onRemove={removeLevel}
            />
          ))}
        </SortableContext>
      </DndContext>

      {canEdit && (
        <Button startIcon={<AddIcon />} onClick={addLevel} variant="outlined" size="small">
          Add Performance Level
        </Button>
      )}
    </Box>
  );
};

export default HolisticEditor;
